import type { TicketSearchResultItem } from '@jq4race/shared';

import type { OutboxItem } from '../../offline/domain/outbox-item.js';

/**
 * findDuplicateInOutbox (coordinator finding #3) — the LOCAL counterpart of
 * `findDuplicateActiveTicket`: a plate already queued in THIS device's own
 * unsynced outbox (same org, same location) is just as real a duplicate as
 * one already on the server, and it is the ONLY kind of duplicate an
 * offline device can ever know about (the server-side check is unreachable
 * with no connectivity). Same normalization as `findDuplicateActiveTicket`
 * so a plate typed either way matches consistently.
 */
export function findDuplicateInOutbox(
  plate: string,
  orgId: string,
  locationId: string,
  items: OutboxItem[],
): OutboxItem | undefined {
  const normalized = plate.trim().toUpperCase();
  return items.find((item) => {
    if (item.orgId !== orgId || item.locationId !== locationId) return false;
    const itemPlate = item.payload.plate;
    return typeof itemPlate === 'string' && itemPlate.trim().toUpperCase() === normalized;
  });
}

/**
 * findDuplicateActiveTicket — pure plate-matching predicate, extracted so
 * the comparison itself is testable without any network mock (Extract-
 * Before-Mock). `searchTickets` already scopes results to ACTIVE tickets at
 * the valet's own location server-side (see its own doc comment in
 * packages/api-client/src/tickets.ts) — this only needs an exact,
 * case-insensitive plate match on top of that.
 */
export function findDuplicateActiveTicket(
  plate: string,
  results: TicketSearchResultItem[],
): TicketSearchResultItem | undefined {
  const normalized = plate.trim().toUpperCase();
  return results.find((r) => r.plate.trim().toUpperCase() === normalized);
}

/**
 * checkDuplicateActivePlate (pwa-offline-first Slice 6 — course correction)
 *
 * Outbox-always (design D5/D12) removed the synchronous `createTicket()` 409
 * that used to power `DuplicatePlateDialog` — but the operator-facing
 * capability itself (warn about a plate that already has an active ticket,
 * let the operator force a deliberate second one) must not silently
 * disappear just because submit no longer calls the backend directly.
 *
 * This is a best-effort, ADVISORY pre-enqueue check — same contract as
 * `useMembershipLookup`'s existing plate-blur lookup: a fast, single
 * network call (never photo uploads, so it does not violate decision #12's
 * "never block on the 4 photo uploads"), swallowed by the caller on any
 * failure. It is NOT the source of truth — the backend's own duplicate
 * detection (Slice 1, `duplicate_active_ticket`) still applies at sync time
 * once Slice 7's SyncEngine lands; this only restores the IMMEDIATE warning
 * for the common online case, and is simply skipped when offline (the
 * caller decides that — see CheckinPage's `isOnline` gate).
 */
export async function checkDuplicateActivePlate(
  plate: string,
  searchTickets: (q: string) => Promise<TicketSearchResultItem[]>,
): Promise<{ existingTicketNumber: number } | null> {
  const results = await searchTickets(plate);
  const match = findDuplicateActiveTicket(plate, results);
  return match ? { existingTicketNumber: match.ticketNumber } : null;
}
